import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PublicNav, Footer } from "@/components/Layout";
import { api } from "@/lib/api";
import { MapPin, Phone, Navigation, Scissors, Star, Award, ChevronRight } from "lucide-react";
import { SIZE_CLASSES, COLOR_CLASSES, STAT_DEFAULTS, TESTIMONIAL_DEFAULTS, getSection, getContent } from "@/lib/sections";

const DAY_LABELS = {
  monday: "Lundi", tuesday: "Mardi", wednesday: "Mercredi",
  thursday: "Jeudi", friday: "Vendredi", saturday: "Samedi", sunday: "Dimanche",
};

export default function Home() {
  const { slug } = useParams();
  const base = slug ? `/salon/${slug}` : "";
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(slug ? `/salon/${slug}` : "/salon").then((r) => setData(r.data)).catch(() => {});
  }, [slug]);

  if (!data) {
    return <div className="min-h-screen bg-[#0A0908] flex items-center justify-center text-[#A39E93] font-mono-caps text-xs">Chargement…</div>;
  }
  const { salon, services } = data;
  const S = (key) => getSection(salon, key);
  const C = getContent(salon);
  const stats = (salon.stats && salon.stats.length) ? salon.stats : STAT_DEFAULTS;
  const testimonials = (salon.testimonials && salon.testimonials.length) ? salon.testimonials : TESTIMONIAL_DEFAULTS;

  return (
    <div className="min-h-screen bg-[#0A0908] text-[#F4F0EA]">
      <PublicNav />

      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center scale-105" style={{ backgroundImage: `url(${salon.banner_url})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0908]/95 via-[#0A0908]/70 to-[#0A0908]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908] via-transparent to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 w-full py-32">
          <div className="font-mono-caps text-xs tracking-[0.4em] text-[#C5A059] mb-6" data-testid="hero-eyebrow">
            ◆ {salon.hero_eyebrow || "Paris · Depuis 2024 · Sur rendez-vous"}
          </div>
          <h1 className="font-display text-6xl sm:text-8xl lg:text-9xl text-[#F4F0EA] max-w-5xl" data-testid="hero-title" style={{ lineHeight: 1 }}>
            <div style={{ marginBottom: "0.35em" }}>{salon.hero_title_line1 || "Coupe nette."}</div>
            <div style={{ marginBottom: "0.35em" }}><span className="text-[#C5A059]">{salon.hero_title_line2 || "Rasage précis."}</span></div>
            <div>{salon.hero_title_line3 || "Style affirmé."}</div>
          </h1>
          <p className="mt-10 text-lg text-[#A39E93] max-w-xl leading-relaxed whitespace-pre-line" data-testid="hero-description">
            {salon.description}
          </p>
          <div className="mt-12 flex flex-wrap gap-4">
            <Link to={`${base}/reservation`} className="btn-primary text-sm" data-testid="hero-book-btn">
              <Scissors className="w-4 h-4" strokeWidth={2} /> Prendre rendez-vous
            </Link>
            <a href={`tel:${salon.phone}`} className="btn-secondary text-sm" data-testid="hero-call-btn">
              <Phone className="w-4 h-4" strokeWidth={2} /> {salon.phone}
            </a>
          </div>
          {C.hero_rating && (
            <div className="mt-16 flex items-center gap-6 text-xs text-[#A39E93]">
              <div className="flex items-center gap-2">
                <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#C5A059] text-[#C5A059]" />)}</div>
                <span className="font-mono-caps tracking-[0.15em]" data-testid="hero-rating">{C.hero_rating}</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* STATS BAR */}
      <section className="border-y border-[#2C2A26] bg-[#0A0908]" data-testid="stats-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#2C2A26]">
          {stats.map((s, i) => (
            <div key={i} className="py-12 px-6 text-center">
              <div className="font-display text-5xl sm:text-6xl text-[#C5A059]">{s.num}</div>
              <div className="font-mono-caps text-xs tracking-[0.3em] text-[#A39E93] mt-3">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="prestations" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
            <div>
              <Eyebrow text={S("services").eyebrow} />
              <SectionTitle cfg={S("services")} />
            </div>
            <Link to={`${base}/reservation`} className="btn-primary text-sm" data-testid="services-book-btn">
              Réserver maintenant <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="services-grid">
            {services.map((s, i) => (
              <Link to={`${base}/reservation?service=${s.id}`} key={s.id}
                className="group block bg-[#141311] border border-[#2C2A26] hover:border-[#C5A059] transition-colors"
                data-testid={`service-card-${i}`}
              >
                <div className="aspect-[4/5] overflow-hidden bg-[#0A0908]">
                  <img src={C.service_images[i] || C.service_images[0]} alt={s.name}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                </div>
                <div className="p-6 lg:p-8">
                  <div className="font-mono-caps text-xs tracking-[0.3em] text-[#C5A059] mb-2">0{i + 1} · {s.duration} min</div>
                  <h3 className="font-display text-3xl mb-3">{s.name}</h3>
                  <div className="flex justify-between items-baseline border-t border-[#2C2A26] pt-4 mt-4">
                    <span className="text-2xl text-[#C5A059]" data-testid={`service-price-${i}`}>{s.price}€</span>
                    <span className="font-mono-caps text-xs tracking-[0.25em] text-[#A39E93] group-hover:text-[#C5A059] inline-flex items-center gap-1" data-testid={`service-book-${i}`}>
                      Réserver <ChevronRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* STYLE GALLERY */}
      <section id="gallery" className="border-t border-[#2C2A26] py-24 lg:py-32 bg-[#0A0908]" data-testid="style-gallery">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
            <div className="max-w-2xl">
              <Eyebrow text={S("gallery").eyebrow} />
              <SectionTitle cfg={S("gallery")} extra="leading-[0.9]" />
              <p className="text-[#A39E93] text-lg mt-6 leading-relaxed whitespace-pre-line" data-testid="gallery-intro">
                {C.gallery_intro}
              </p>
            </div>
            <Link to={`${base}/reservation`} className="btn-secondary text-sm" data-testid="gallery-book-btn">
              Tout réserver <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="gallery-grid">
            {C.gallery.map((g, i) => {
              const service = services.find((s) => s.name === g.service);
              const href = service ? `${base}/reservation?service=${service.id}` : `${base}/reservation`;
              return (
                <Link to={href} key={i}
                  className="group relative block aspect-[3/4] overflow-hidden bg-[#141311] border border-[#2C2A26] hover:border-[#C5A059] transition-colors"
                  data-testid={`gallery-card-${i}`}
                >
                  <img src={g.img} alt={g.name}
                    className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908] via-[#0A0908]/30 to-transparent" />
                  <div className="absolute inset-0 p-5 flex flex-col justify-end">
                    <div className="font-mono-caps text-[10px] tracking-[0.3em] text-[#C5A059] mb-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      ◆ {g.service} {service ? `· ${service.price}€` : ""}
                    </div>
                    <h3 className="font-display text-2xl sm:text-3xl text-[#F4F0EA] leading-tight">{g.name}</h3>
                    <p className="text-xs text-[#A39E93] mt-1.5 max-w-[90%] whitespace-pre-line">{g.desc}</p>
                    <div className="mt-3 inline-flex items-center gap-1.5 font-mono-caps text-[10px] tracking-[0.3em] text-[#C5A059] translate-x-0 group-hover:translate-x-1 transition-transform">
                      Réserver <ChevronRight className="w-3 h-3" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* QUI SOMMES-NOUS / VALEURS / ENGAGEMENT */}
      <section id="about" className="border-t border-[#2C2A26] py-24 lg:py-32 bg-[#0A0908]" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Intro grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center mb-24">
            <div className="md:col-span-7">
              <img src={C.about_image} alt="L'atelier" className="w-full h-[600px] object-cover" />
            </div>
            <div className="md:col-span-5">
              <Eyebrow text={S("about").eyebrow} />
              <SectionTitle cfg={S("about")} extra="mb-8 leading-[0.95]" />
              {C.about_paragraphs.map((p, i) => (
                <p key={i} className="text-[#A39E93] text-lg leading-relaxed mb-5 whitespace-pre-line" data-testid={`about-paragraph-${i}`}>{p}</p>
              ))}
            </div>
          </div>

          {/* Nos valeurs */}
          <div className="mb-24" data-testid="values-grid">
            <div className="text-center mb-16">
              <Eyebrow text={S("values").eyebrow} />
              <SectionTitle cfg={S("values")} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[#2C2A26]">
              {C.values.map((v, i) => (
                <div key={i} className="bg-[#0A0908] p-8 lg:p-10" data-testid={`value-card-${i}`}>
                  <div className="font-display text-4xl text-[#C5A059] mb-4">{v.num}</div>
                  <h3 className="font-display text-2xl mb-3">{v.title}</h3>
                  <p className="text-[#A39E93] text-sm leading-relaxed whitespace-pre-line">{v.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Notre engagement */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center" data-testid="commitment">
            <div className="md:col-span-5 md:order-2">
              <img src={C.commitment_image} alt="Notre engagement" className="w-full h-[600px] object-cover grayscale" />
            </div>
            <div className="md:col-span-7 md:order-1">
              <Eyebrow text={S("commitment").eyebrow} />
              <SectionTitle cfg={S("commitment")} extra="mb-10 leading-[0.95]" />
              <ul className="space-y-5">
                {C.commitment_items.map((e, i) => (
                  <li key={i} className="flex gap-5 pb-5 border-b border-[#2C2A26] last:border-0" data-testid={`commitment-item-${i}`}>
                    <div className="font-display text-2xl text-[#C5A059] shrink-0">◆</div>
                    <div>
                      <div className="font-display text-xl mb-1">{e.title}</div>
                      <p className="text-[#A39E93] leading-relaxed whitespace-pre-line">{e.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex flex-wrap gap-6 text-sm">
                {C.commitment_tags.map((tag, i) => (
                  <span key={i} className="font-mono-caps text-xs tracking-[0.2em] text-[#A39E93] border-b border-[#2C2A26] pb-2">{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-t border-[#2C2A26] py-24 lg:py-32" data-testid="testimonials">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <Eyebrow text={S("testimonials").eyebrow} />
            <SectionTitle cfg={S("testimonials")} />
            {C.testimonials_rating && (
              <div className="flex justify-center items-center gap-3 mt-6">
                <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-[#C5A059] text-[#C5A059]" />)}</div>
                <span className="font-mono-caps text-sm tracking-[0.2em] text-[#A39E93]" data-testid="testimonials-rating">{C.testimonials_rating}</span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-[#141311] border border-[#2C2A26] p-8" data-testid={`testimonial-${i}`}>
                <div className="flex mb-4">{[...Array(t.stars)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#C5A059] text-[#C5A059]" />)}</div>
                <p className="text-[#F4F0EA] text-lg leading-relaxed mb-6 whitespace-pre-line">« {t.text} »</p>
                <div className="font-mono-caps text-xs tracking-[0.25em] text-[#A39E93]">— {t.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AWARDS */}
      {C.awards.length > 0 && (
        <section className="border-t border-[#2C2A26] py-16" data-testid="awards">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-wrap items-center justify-center gap-x-12 gap-y-6 text-center">
            {C.awards.map((a, i) => (
              <div key={i} className="flex items-center gap-3 text-[#A39E93]">
                <Award className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
                <span className="font-mono-caps text-xs tracking-[0.3em]">{a}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CONTACT / HOURS */}
      <section id="contact" className="border-t border-[#2C2A26] py-24 lg:py-32 bg-[#0A0908]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Eyebrow text={S("contact").eyebrow} />
            <SectionTitle cfg={S("contact")} extra="leading-[0.9] mb-10" />
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-[#C5A059] mt-1 shrink-0" strokeWidth={2} />
                <div>
                  <div className="font-mono-caps text-xs tracking-[0.3em] text-[#6B6760] mb-1">Adresse</div>
                  <div className="text-[#F4F0EA] text-lg" data-testid="contact-address">{salon.address}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="w-5 h-5 text-[#C5A059] mt-1 shrink-0" strokeWidth={2} />
                <div>
                  <div className="font-mono-caps text-xs tracking-[0.3em] text-[#6B6760] mb-1">Téléphone</div>
                  <a href={`tel:${salon.phone}`} className="text-[#F4F0EA] text-lg hover:text-[#C5A059]" data-testid="contact-phone">{salon.phone}</a>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 pt-6">
                <a href={salon.map_url} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm" data-testid="contact-directions">
                  <Navigation className="w-4 h-4" strokeWidth={2} /> Itinéraire
                </a>
                <Link to={`${base}/reservation`} className="btn-primary text-sm" data-testid="contact-book">
                  <Scissors className="w-4 h-4" strokeWidth={2} /> Réserver
                </Link>
              </div>
            </div>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            <Eyebrow text={S("hours").eyebrow} />
            <SectionTitle cfg={S("hours")} extra="mb-10" />
            <div className="divide-y divide-[#2C2A26] border-y border-[#2C2A26]" data-testid="hours-list">
              {Object.entries(DAY_LABELS).map(([key, label]) => {
                const h = salon.hours?.[key] || { open: false, ranges: [] };
                return (
                  <div key={key} className="flex justify-between items-center py-4">
                    <span className="font-mono-caps text-sm tracking-[0.25em] text-[#F4F0EA]">{label}</span>
                    <span className="text-[#A39E93]">
                      {h.open && h.ranges?.length
                        ? h.ranges.map((r) => `${r.start} – ${r.end}`).join(" · ")
                        : <span className="font-mono-caps text-xs tracking-[0.3em] text-[#6B6760]">Fermé</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-[#2C2A26] py-24 bg-[#141311]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <SectionTitle cfg={S("cta")} extra="mb-6" />
          {C.cta_subtitle && <p className="text-[#A39E93] text-lg mb-10 whitespace-pre-line" data-testid="cta-subtitle">{C.cta_subtitle}</p>}
          <Link to={`${base}/reservation`} className="btn-primary text-sm inline-flex" data-testid="final-cta">
            <Scissors className="w-4 h-4" strokeWidth={2} /> Prendre rendez-vous
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function renderTitleLines(title) {
  return (title || "").split(/\||\n/).map((line, i, arr) => (
    <span key={i}>
      {line}
      {i < arr.length - 1 && <br />}
    </span>
  ));
}

function Eyebrow({ text }) {
  if (!text) return null;
  return <div className="font-mono-caps text-xs tracking-[0.4em] text-[#C5A059] mb-4">◆ {text}</div>;
}

function SectionTitle({ cfg, extra = "" }) {
  const sizeClass = SIZE_CLASSES[cfg.size] || SIZE_CLASSES.L;
  const colorClass = COLOR_CLASSES[cfg.color] || COLOR_CLASSES.white;
  return (
    <h2 className={`font-display ${sizeClass} ${colorClass} ${extra}`}>
      {renderTitleLines(cfg.title)}
    </h2>
  );
}
